/**
 * Bodega: qué se lleva una orden y qué tiene que regresar.
 *
 * Cuando haya varias instalaciones simultáneas con distinto personal, nadie puede
 * depender de la memoria para saber qué cargar — y todo lo que sale de la bodega
 * tiene que volver, o hacerse notorio que no volvió. Este módulo arma la lista de
 * salida a partir de lo que la cotización ya sabe:
 *
 *   · productos  — lo que se va a instalar (consolidado por sku entre habitaciones)
 *   · material   — la lista que el motor ya calcula para almacén y compras
 *   · herramientas — la unión, sin duplicados, de las que declara cada producto
 *
 * La regla de cierre es distinta por tipo, y el documento la respeta: el material
 * se CONSUME en la obra (lo que vuelve es sobrante, no faltante), el producto se
 * QUEDA instalado, y la herramienta REGRESA SIEMPRE — su diferencia sí es un
 * faltante con responsable.
 *
 * Los protocolos guiados (punto 7 de la hoja de ruta) también declararán
 * herramientas; cuando existan, su unión entra aquí mismo.
 */
import { materiales } from './calculo.js'

/** Ids de herramienta, sin duplicar, que piden los productos activos de la cotización. */
export function herramientasDeCotizacion(cot, ctx) {
  const ids = new Set()
  for (const h of cot.habitaciones || []) {
    for (const it of h.items || []) {
      if (!(it.e1 || it.e2 || it.e3)) continue
      const p = ctx.P.get(it.sku)
      for (const id of p?.herr || []) ids.add(id)
    }
  }
  return [...ids]
}

/**
 * Renglones iniciales de la salida de bodega. Cada renglón: tipo, clave, nombre,
 * cantidad que sale y cantidad que vuelve (se captura al regreso).
 */
export function renglonesDeSalida(cot, ctx, herramientas = []) {
  const renglones = []

  // Productos, consolidados por sku: el mismo producto en dos habitaciones es
  // una sola línea de carga.
  const porSku = new Map()
  for (const h of cot.habitaciones || []) {
    for (const it of h.items || []) {
      if (!(it.e1 || it.e2 || it.e3)) continue
      porSku.set(it.sku, (porSku.get(it.sku) || 0) + (it.q || 1))
    }
  }
  for (const [sku, q] of porSku) {
    const p = ctx.P.get(sku) || {}
    renglones.push({ tipo: 'producto', clave: sku, nombre: p.nombre || sku, sale: q, vuelve: 0 })
  }

  for (const m of materiales(cot, ctx)) {
    renglones.push({ tipo: 'material', clave: m.sku, nombre: m.nombre, sale: Math.ceil(m.q), vuelve: 0 })
  }

  for (const id of herramientasDeCotizacion(cot, ctx)) {
    const her = herramientas.find(x => x.id === id)
    renglones.push({ tipo: 'herramienta', clave: id, nombre: her?.nombre || id, sale: 1, vuelve: 0 })
  }

  return renglones
}

export const NOMBRE_TIPO_RENGLON = {
  producto: 'Producto',
  material: 'Material',
  herramienta: 'Herramienta',
  extra: 'Extra'
}
